// web/src/components/Hero.tsx
// Generic page-top hero. Lighter weight than CoverHero — no photo, no
// stickers, no SubDeck. Used on Stats, Achievements, KidDetail headers
// when we want a strong title without going full magazine-cover.

import type { ReactNode, CSSProperties } from 'react';

export interface HeroProps {
  /** Big headline text in display Fraunces. */
  title: ReactNode;
  /** Small-caps tagline above the title (kicker). */
  kicker?: string;
  /** Body text below the title. */
  subtitle?: ReactNode;
  /** Visual treatment. */
  tone?: 'light' | 'dark';
  /** Right-aligned slot for actions (button, badge). */
  actions?: ReactNode;
  style?: CSSProperties;
}

export function Hero({ title, kicker, subtitle, tone = 'light', actions, style }: HeroProps) {
  const isDark = tone === 'dark';
  return (
    <header
      style={{
        background: isDark ? 'var(--surface-3)' : 'transparent',
        color: isDark ? 'var(--ink-on-green)' : 'var(--ink)',
        padding: isDark ? 'var(--space-xl) var(--space-lg)' : '0',
        marginBottom: 'var(--space-lg)',
        ...style,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 'var(--space-base)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          {kicker ? (
            <div
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--micro)',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: isDark ? 'var(--gold-light)' : 'var(--muted)',
                marginBottom: 'var(--space-xs)',
              }}
            >
              {kicker}
            </div>
          ) : null}
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--headline)',
              fontWeight: 500,
              color: isDark ? 'var(--ink-on-green)' : 'var(--green)',
              margin: 0,
              lineHeight: 1.05,
            }}
          >
            {title}
          </h1>
          {subtitle ? (
            <div
              style={{
                marginTop: 'var(--space-sm)',
                fontSize: 'var(--lede)',
                color: isDark ? 'rgba(250,245,236,0.8)' : 'var(--muted)',
                lineHeight: 1.45,
              }}
            >
              {subtitle}
            </div>
          ) : null}
        </div>
        {actions ? <div style={{ flexShrink: 0 }}>{actions}</div> : null}
      </div>
    </header>
  );
}
